import { useState } from "react";

import { Button, ErrorAlert, Modal } from "@/components/ui";
import type { DnsChange, DnsPreview } from "@/lib/domains";

import { needsAcknowledgement, PreviewBody } from "./PreviewBody";
import { usesPriority } from "./util";

/** Figma 4724-7245 "Review DNS change": the diff, the conflicts, the impact, then apply. */
export function ReviewModal({
  open,
  fqdn,
  change,
  preview,
  onBack,
  onClose,
  onConfirm,
  applying,
  error,
}: {
  open: boolean;
  fqdn: string;
  change: DnsChange;
  preview: DnsPreview;
  onBack: () => void;
  onClose: () => void;
  onConfirm: () => void;
  applying: boolean;
  error: unknown;
}) {
  const [understood, setUnderstood] = useState(false);
  const ack = needsAcknowledgement(preview);
  const verb = change.op === "add" ? "Add" : change.op === "update" ? "Change" : "Remove";

  return (
    <Modal
      open={open}
      onClose={onClose}
      width={560}
      title="Review DNS change"
      subtitle={`Check what changes before anything is applied to ${fqdn}.`}
      note="Unrelated records are kept. Changes show as Pending until the DNS provider confirms."
      footer={
        <>
          <Button variant="secondary" size="lg" onClick={onBack}>
            Back to edit
          </Button>
          <Button
            variant="brand"
            size="lg"
            loading={applying}
            disabledReason={
              ack && !understood ? "Tick the box to confirm you understand the impact" : undefined
            }
            onClick={onConfirm}
          >
            Confirm and apply
          </Button>
        </>
      }
    >
      <div className="flex flex-wrap items-center gap-2 rounded-[10px] border border-border bg-[#fefaf8] px-3 py-2.5 text-[13px] text-fg">
        <span className="rounded-md bg-[#221c19] px-1.5 py-0.5 text-[11px] font-semibold text-white">
          {change.type}
        </span>
        <span className="min-w-0 break-all">
          {verb} {change.name} → {change.content}
          {usesPriority(change.type ?? "") && change.priority !== null && change.priority !== undefined
            ? ` · priority ${change.priority}`
            : ""}
          {change.op !== "delete" && change.ttl ? ` · TTL ${change.ttl}` : ""}
        </span>
      </div>

      <PreviewBody fqdn={fqdn} preview={preview} understood={understood} onUnderstoodChange={setUnderstood} />
      <ErrorAlert error={error} />
    </Modal>
  );
}
